import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import PublicLayout from '../../components/layouts/PublicLayout';
import ProductGrid from '../../components/products/ProductGrid';
import ProductFilters from '../../components/products/ProductFilters';
import Pagination from '../../components/common/Pagination';
import Modal from '../../components/common/Modal';
import Button from '../../components/common/Button';
import { FilterIcon, PhoneIcon, UserIcon, CameraIcon, HomeIcon, StarIcon, CheckCircleIcon, FileIcon, HeartIcon, TagIcon } from '../../components/icons/Icons';
import { useProducts } from '../../hooks/useProducts';
import { useProductFilters } from '../../hooks/useProductFilters';
import { categoryService } from '../../services/category.service';
import { theme } from '../../config/theme';
import { motion, AnimatePresence } from 'framer-motion';
import GiveawaySection from '../../components/ads/GiveawaySection';
import { adService } from '../../services/ad.service';
import { getImageUrl } from '../../utils/productHelpers';

const PublicHome = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, role } = useAuth();
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [activeAd, setActiveAd] = useState(null);
  const [giveawaySettings, setGiveawaySettings] = useState(null);

  useEffect(() => {
    const fetchBannerAd = async () => {
      try {
        const response = await adService.getActiveAds('homepage_banner');
        const payload = response?.data || response;
        if (payload?.ads && payload.ads.length > 0) {
          setActiveAd(payload.ads[0]);
        }
      } catch (error) {
        console.error('Failed to fetch homepage banner ad:', error);
      }
    };

    const fetchGiveawayDetails = async () => {
      try {
        const response = await adService.getGiveawaySettings();
        const payload = response?.data || response;
        setGiveawaySettings(payload);
      } catch (error) {
        console.error('Failed to fetch giveaway settings:', error);
      }
    };

    fetchBannerAd();
    fetchGiveawayDetails();
  }, []);

  const initialSearch = searchParams.get('search') || '';

  const { filters, activeFiltersCount, updateFilter, clearAllFilters, getActiveFilters } = useProductFilters({ search: initialSearch });
  const { products, pagination, loading, goToPage, updateFilters: setApiFilters } = useProducts(getActiveFilters());

  // Sync the UI filters state with the API fetching state
  useEffect(() => {
    setApiFilters(getActiveFilters());

    // Optional: Keep URL clean or updated based on search params
    if (filters.search !== initialSearch) {
      if (filters.search) {
        setSearchParams({ search: filters.search }, { replace: true });
      } else {
        setSearchParams({}, { replace: true });
      }
    }
  }, [filters, setApiFilters, setSearchParams]);

  // Slider State
  const [currentSlide, setCurrentSlide] = useState(0);

  // DB Categories cache to match requested categories
  const [dbCategories, setDbCategories] = useState([]);

  useEffect(() => {
    if (isAuthenticated) {
      if (role === 'seller') {
        navigate('/seller/dashboard');
      } else if (role === 'admin') {
        navigate('/admin/dashboard');
      }
    }
  }, [isAuthenticated, role, navigate]);

  useEffect(() => {
    categoryService.getAll({ is_active: true })
      .then(res => setDbCategories(res.data?.categories || []))
      .catch(console.error);
  }, []);

  // Hero Slider Effect
  const slides = [
    {
      id: 1,
      title: 'Experience Premium Quality',
      subtitle: 'Shop the latest trends with exclusive deals on AlarcTrade. Free shipping on orders over ₹100!',
      image: '/images/hero_1.png',
      bgFallback: `linear-gradient(135deg, ${theme.colors.primary.main} 0%, ${theme.colors.primary.dark} 100%)`,
    },
    {
      id: 2,
      title: 'Summer Fashion Sale',
      subtitle: 'Up to 50% off on all summer apparel. Refresh your wardrobe today and stand out.',
      image: '/images/hero_2.png',
      bgFallback: `linear-gradient(135deg, #FF6B6B 0%, #C0392B 100%)`,
    },
    {
      id: 3,
      title: 'Next-Gen Electronics',
      subtitle: 'Upgrade your tech with our latest gadgets, smart devices, and futuristic accessories.',
      image: '/images/hero_3.png',
      bgFallback: `linear-gradient(135deg, #1E88E5 0%, #0D47A1 100%)`,
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const handleFilterChange = (key, value) => {
    updateFilter(key, value);
  };

  const handleCategoryClick = (categoryName) => {
    // Try to find the category in the database
    const matchingCat = dbCategories.find(c => c.name.toLowerCase().includes(categoryName.toLowerCase()) || categoryName.toLowerCase().includes(c.name.toLowerCase()));

    // Clear other key filters first to start fresh
    clearAllFilters();

    if (matchingCat) {
      updateFilter('category_id', matchingCat.id);
    } else {
      // Fallback: use text search if no specific category id exists
      updateFilter('search', categoryName);
    }

    // Scroll down to products
    document.getElementById('product-grid')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Skip rendering if checking auth redirect for sellers/admins
  if (isAuthenticated && role !== 'customer') return null;

  const categoryStyles = {
    'Mobiles': { icon: PhoneIcon, color: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
    'Fashion': { icon: UserIcon, color: 'linear-gradient(135deg, #ff0844 0%, #ffb199 100%)' },
    'Electronics': { icon: CameraIcon, color: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' },
    'Home': { icon: HomeIcon, color: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' },
    'Beauty': { icon: StarIcon, color: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)' },
    'Sports': { icon: CheckCircleIcon, color: 'linear-gradient(135deg, #ff9a44 0%, #fc6076 100%)' },
    'Books': { icon: FileIcon, color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
    'Toys': { icon: HeartIcon, color: 'linear-gradient(135deg, #fccb90 0%, #d57eeb 100%)' },
    'default': { icon: TagIcon, color: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)' }
  };

  const quickCategories = [
    { name: 'Mobiles' }, { name: 'Fashion' }, { name: 'Electronics' }, { name: 'Home' },
    { name: 'Beauty' }, { name: 'Sports' }, { name: 'Books' }, { name: 'Toys' }
  ];

  const displayCategories = dbCategories && dbCategories.length > 0
    ? dbCategories.slice(0, 8)
    : quickCategories;

  const heroStyles = {
    borderRadius: theme.borderRadius['2xl'] || '1rem',
    color: theme.colors.white,
    textAlign: 'center',
    marginBottom: theme.spacing['3xl'] || '3rem',
    marginTop: '34px', // Space for announcement bar
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 20px 40px -10px rgba(0,0,0,0.3)',
    minHeight: '480px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  };

  const titleStyles = {
    fontSize: 'clamp(2.5rem, 5vw, 4.5rem)',
    fontWeight: theme.typography.fontWeight.bold,
    marginBottom: theme.spacing.lg,
    lineHeight: 1.1,
    letterSpacing: '-1px',
    textShadow: '0 4px 20px rgba(0,0,0,0.5)',
  };

  const subtitleStyles = {
    fontSize: theme.typography.fontSize.xl,
    opacity: 0.95,
    marginBottom: theme.spacing['2xl'] || '2rem',
    maxWidth: '650px',
    margin: `0 auto ${theme.spacing['2xl'] || '2rem'}`,
    lineHeight: 1.6,
    textShadow: '0 2px 10px rgba(0,0,0,0.5)',
  };

  return (
    <PublicLayout>
      <div style={{ padding: theme.spacing.xl, maxWidth: '1400px', margin: '0 auto' }}>

        {/* Hero Slider Section */}
        <div style={heroStyles}>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
              style={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                backgroundImage: `url(${slides[currentSlide].image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                zIndex: 0
              }}
            >
              <div style={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.6) 100%)',
              }} />
            </motion.div>
          </AnimatePresence>

          <div style={{ position: 'relative', zIndex: 1, width: '100%', padding: '0 20px' }}>
            <AnimatePresence mode="wait">
              <motion.h1
                key={`title-${currentSlide}`}
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -30, opacity: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                style={titleStyles}
              >
                {slides[currentSlide].title}
              </motion.h1>
              <motion.p
                key={`subtitle-${currentSlide}`}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -20, opacity: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                style={subtitleStyles}
              >
                {slides[currentSlide].subtitle}
              </motion.p>
              <motion.div
                key={`btn-${currentSlide}`}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -20, opacity: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} style={{ display: 'inline-block' }}>
                  <Button
                    size="lg"
                    variant="secondary"
                    onClick={() => {
                      document.getElementById('product-grid').scrollIntoView({ behavior: 'smooth' });
                    }}
                    style={{
                      backgroundColor: theme.colors.white,
                      color: theme.colors.primary.main,
                      fontWeight: theme.typography.fontWeight.bold,
                      padding: `${theme.spacing.lg} ${theme.spacing['3xl'] || '2.5rem'}`,
                      fontSize: theme.typography.fontSize.lg,
                      borderRadius: theme.borderRadius.full,
                      boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                      border: 'none',
                    }}
                  >
                    Shop Now
                  </Button>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Slider Indicators */}
          <div style={{ position: 'absolute', bottom: '30px', display: 'flex', gap: '12px', zIndex: 2 }}>
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  border: currentSlide === index ? `2px solid ${theme.colors.white}` : 'none',
                  backgroundColor: currentSlide === index ? 'transparent' : 'rgba(255,255,255,0.5)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  transform: currentSlide === index ? 'scale(1.2)' : 'scale(1)',
                }}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Categories Quick Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          style={{
            marginBottom: theme.spacing['2xl'],
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
            gap: theme.spacing.lg,
            justifyItems: 'center'
          }}>
          {displayCategories.map((cat, index) => {
            // Check for matching styles or use random/default based on name layout
            const style = categoryStyles[cat.name] || categoryStyles.default;
            const Icon = style.icon;

            return (
              <motion.div
                key={cat.id || cat.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 * index }}
                whileHover={{ y: -8, boxShadow: theme.shadows.md, borderColor: theme.colors.primary.light }}
                onClick={() => handleCategoryClick(cat.name)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: theme.spacing.md,
                  padding: theme.spacing.lg,
                  backgroundColor: theme.colors.white,
                  borderRadius: theme.borderRadius.xl,
                  boxShadow: theme.shadows.sm,
                  cursor: 'pointer',
                  border: `1px solid ${theme.colors.gray[100]}`,
                  width: '100%',
                  maxWidth: '150px'
                }}
              >
                <div style={{
                  width: '72px',
                  height: '72px',
                  borderRadius: '50%',
                  background: style.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: theme.colors.white,
                  boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                  overflow: 'hidden'
                }}>
                  {cat.image ? (
                    <img 
                      src={cat.image} 
                      alt={cat.name} 
                      style={{ 
                        width: '40px', 
                        height: '40px', 
                        objectFit: 'contain',
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                      }} 
                    />
                  ) : (
                    <Icon size={28} />
                  )}
                </div>
                <span style={{
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.colors.text.primary,
                  fontSize: theme.typography.fontSize.base,
                  letterSpacing: '0.3px',
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%'
                }}>
                  {cat.name}
                </span>
              </motion.div>
            );
          })}
        </motion.div>

        <div id="product-grid" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.spacing.xl,
          paddingTop: theme.spacing.xl, // For anchor scrolling offset
        }}>
          <h1 style={{
            fontSize: theme.typography.fontSize['3xl'],
            fontWeight: theme.typography.fontWeight.bold,
          }}>
            Shop Products
          </h1>
          <Button variant="outline" onClick={() => setIsFilterModalOpen(true)}>
            <FilterIcon size={20} />
            Filters {activeFiltersCount > 0 && `(${activeFiltersCount})`}
          </Button>
        </div>

        <div>
          <div style={{
            marginBottom: theme.spacing.md,
            fontSize: theme.typography.fontSize.sm,
            color: theme.colors.text.secondary,
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing.sm
          }}>
            {pagination.total || 0} products found
            {activeFiltersCount > 0 && (
              <Button variant="ghost" size="sm" onClick={clearAllFilters} style={{ padding: '0 8px', color: theme.colors.error }}>
                Clear Filters
              </Button>
            )}
          </div>

          <ProductGrid products={products} loading={loading} />

          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            hasNextPage={pagination.hasNextPage}
            hasPreviousPage={pagination.hasPreviousPage}
            totalItems={pagination.total}
            itemsPerPage={pagination.limit}
            onPageChange={goToPage}
          />
        </div>

        {/* Ad Banner + Giveaway Section */}
        <GiveawaySection
          adImageUrl={activeAd ? getImageUrl(activeAd.image_url) : undefined}
          adLinkUrl={activeAd ? activeAd.link_url : undefined}
          adTitle={activeAd?.title || "Exclusive Deals from Our Partners"}
          giftImageUrl={giveawaySettings?.image_url ? getImageUrl(giveawaySettings.image_url) : undefined}
          giveawayTitle={giveawaySettings?.title}
          giveawayDescription={giveawaySettings?.description}
          onAdClick={() => {
            if (activeAd) {
              adService.trackClick(activeAd.id).catch(err => console.error('Error tracking click:', err));
            }
          }}
          onPhoneSubmit={async (phone) => {
            try {
              await adService.submitGiveaway(phone);
            } catch (err) {
              console.error('Error submitting giveaway entry:', err);
              throw err;
            }
          }}
        />

        {/* Features Section */}
        <div style={{ marginTop: '5rem', marginBottom: '2rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: theme.spacing.xl }}>
          {[
            { title: 'Free Shipping', desc: 'On all orders over ₹100', icon: '🚚', color: '#ecfdf5' },
            { title: 'Secure Payment', desc: '100% secure checkout', icon: '🔒', color: '#eff6ff' },
            { title: '24/7 Support', desc: 'Dedicated support team', icon: '💡', color: '#fef2f2' },
            { title: 'Easy Returns', desc: '30-day return policy', icon: '🔄', color: '#f5f3ff' },
          ].map((feature, i) => (
            <motion.div
              key={i}
              whileHover={{ y: -8 }}
              style={{
                backgroundColor: feature.color,
                padding: theme.spacing['2xl'],
                borderRadius: theme.borderRadius.xl,
                textAlign: 'center',
                boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div style={{ fontSize: '3rem', marginBottom: theme.spacing.md }}>{feature.icon}</div>
              <h3 style={{ fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary, marginBottom: theme.spacing.xs }}>{feature.title}</h3>
              <p style={{ color: theme.colors.text.secondary, fontSize: theme.typography.fontSize.sm }}>{feature.desc}</p>
            </motion.div>
          ))}
        </div>

        <Modal
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          title="Filter Products"
          size="md"
        >
          <ProductFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={clearAllFilters}
            activeCount={activeFiltersCount}
          />
        </Modal>
      </div>
    </PublicLayout>
  );
};

export default PublicHome;